import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getFinancialStatementBook8Column } from '../../../../../../lib/api/accounting';
import CenteredCircularProgress from '../../../../../../lib/designSystem/CircularProgress';
import PageHeader, {
  ActionButton,
} from '../../../../../../lib/designSystem/PageHeader';
import {
  formatDate,
  formatDateString,
} from '../../../../../../lib/functions/utils';
import { RootState } from '../../../../../../services/redux/store';
import { useAccountingPeriod } from '../../context/AccountingPeriodContext';
import { toDateStringSafe } from '../utils/dateUtils';
import { fetchWithDateChunking } from '../utils/fetchWithDateChunking';
import {
  deepMergeGroupedData,
  mergeSummedLines,
} from '../utils/mergeStrategies';
import FinancialStatementBookEntriesTable from './components';

dayjs.extend(utc);

import ExcelJS from 'exceljs';

const exportToExcel = async (
  entries: any[],
  startDate: Date,
  endDate: Date,
  companyIdentifier?: string,
  companyName?: string,
) => {
  // Mapeo de datos planos
  const plainData = entries.map((line: any) => {
    return [
      line.account, // Fecha
      line.accountName, // Rut cliente
      line.debit, // Débito
      line.credit, // Crédito
      line.debtor, // Débito final
      line.creditor, // Crédito final
      line.active, // Activo
      line.passive, // Pasivo
      line.loss, // Pérdida
      line.income, // Ingreso
    ];
  });
  const totals = plainData.reduce(
    (acc: any, line: any) => {
      acc.debit += line[2];
      acc.credit += line[3];
      acc.debtor += line[4];
      acc.creditor += line[5];
      acc.active += line[6];
      acc.passive += line[7];
      acc.loss += line[8];
      acc.income += line[9];
      return acc;
    },
    {
      debit: 0,
      credit: 0,
      debtor: 0,
      creditor: 0,
      active: 0,
      passive: 0,
      loss: 0,
      income: 0,
    },
  );

  const sums = [
    'Sumas totales',
    '',
    totals.debit,
    totals.credit,
    totals.debtor,
    totals.creditor,
    totals.active,
    totals.passive,
    totals.loss,
    totals.income,
  ];

  const result = [
    'Resultado',
    '',
    '',
    '',
    '',
    '',
    totals.active < totals.passive ? totals.passive - totals.active : 0,
    totals.passive < totals.active ? totals.active - totals.passive : 0,
    totals.loss < totals.income ? totals.income - totals.loss : 0,
    totals.income < totals.loss ? totals.loss - totals.income : 0,
  ];

  const equals = [
    'Totales iguales',
    '',
    totals.debit,
    totals.credit,
    totals.debtor,
    totals.creditor,
    totals.active > totals.passive ? totals.active : totals.passive,
    totals.passive > totals.active ? totals.passive : totals.active,
    totals.loss > totals.income ? totals.loss : totals.income,
    totals.income > totals.loss ? totals.income : totals.loss,
  ];

  // Encabezados de la hoja de Excel
  const headers = [
    'Cuenta',
    'Nombre de Cuenta',
    'Debe',
    'Haber',
    'Débito',
    'Crédito',
    'Activo',
    'Pasivo',
    'Pérdida',
    'Ganancia',
  ];

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Sheet1');

  // Filas de información inicial
  const infoRows = [
    ['Informacion de la consulta', ''],
    ['Razón social:', companyName || 'N/A'],
    ['RUT:', companyIdentifier || 'N/A'],
    ['Tipo de balance:', '8 Columnas'],
    ['Periodo:', `${formatDate(startDate)} a ${formatDate(endDate)}`],
    ['Fecha de consulta:', formatDate(new Date())],
    [], // Fila vacía como separador
  ];

  // Agregar filas de información
  infoRows.forEach((rowData) => {
    const row = worksheet.addRow(rowData);
    // Formato para las filas de información (primera columna en negrita)
    if (rowData.length > 0) {
      const firstCell = row.getCell(1);
      firstCell.font = { bold: true };
    }
  });

  const rows = [headers, ...plainData, sums, result, equals];

  // Agregar datos a la hoja
  rows.forEach((rowData, rowIndex) => {
    const row = worksheet.addRow(rowData);

    if (rowIndex === 0) {
      // Formato para la primera fila (encabezados)
      row.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        worksheet.getColumn(cell.col).width = 20; // Ajustar ancho de columna
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFDDDDDD' }, // Color gris claro
        };
      });
    }
  });

  // Guardar el archivo
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);

  // Descargar el archivo
  const a = document.createElement('a');
  a.href = url;
  a.download = `Balance_General_${formatDate(startDate)}_${formatDate(endDate)}.xlsx`;
  a.click();

  URL.revokeObjectURL(url);
};

const FinancialStatementBook: React.FC<{
  transactional?: boolean;
  startDate?: Date;
  endDate?: Date;
}> = ({
  transactional = false,
  startDate: propStartDate,
  endDate: propEndDate,
}) => {
  const {
    startDate: contextStart,
    endDate: contextEnd,
    setPeriod,
  } = useAccountingPeriod();
  const hasProps = propStartDate != null && propEndDate != null;

  const [localStartDate, setLocalStartDate] = useState<string | null>(
    hasProps && propStartDate
      ? (toDateStringSafe(propStartDate) ?? contextStart)
      : contextStart,
  );
  const [localEndDate, setLocalEndDate] = useState<string | null>(
    hasProps && propEndDate
      ? (toDateStringSafe(propEndDate) ?? contextEnd)
      : contextEnd,
  );
  const [filterStartDate, setFilterStartDate] = useState<string | null>(
    contextStart,
  );
  const [filterEndDate, setFilterEndDate] = useState<string | null>(contextEnd);

  const startDate = hasProps ? localStartDate : contextStart;
  const endDate = hasProps ? localEndDate : contextEnd;
  const filterValueStart = hasProps ? localStartDate : filterStartDate;
  const filterValueEnd = hasProps ? localEndDate : filterEndDate;

  const [data, setData] = useState<any>({ summedLines: [] });
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const hasLoadedInitial = useRef(false);

  const setUp = async (
    startDateParam: string,
    endDateParam: string,
    showLoading: boolean = true,
  ) => {
    if (hasProps) {
      setLocalStartDate(startDateParam);
      setLocalEndDate(endDateParam);
    }

    try {
      if (showLoading) setLoading(true);
      const result = await fetchWithDateChunking({
        startDate: startDateParam,
        endDate: endDateParam,
        thresholdMonths: 6,
        apiFunction: getFinancialStatementBook8Column,
        mergeStrategy: (responses) => {
          const allSummedResponses = responses.map(
            (response) => response?.summedLines || [],
          );
          const allGroupedResponses = responses.map(
            (response) => response?.groupedByCategoryAndBusiness || {},
          );

          const mergedSummedLines = mergeSummedLines(allSummedResponses);
          const sortedMergedSummedLines = mergedSummedLines.sort((a, b) =>
            a.account.localeCompare(b.account),
          );

          return {
            summedLines: sortedMergedSummedLines,
            groupedByCategoryAndBusiness:
              deepMergeGroupedData(allGroupedResponses),
          };
        },
        additionalParams: { transactional },
        dispatch,
      });

      setData(result);
    } catch (error) {
      console.error('Error fetching financial statement book:', error);
    }
    if (showLoading) setLoading(false);
  };

  const refreshData = async (showLoading: boolean = true) => {
    if (startDate && endDate) {
      await setUp(startDate, endDate, showLoading);
    }
  };

  useEffect(() => {
    if (!hasProps) {
      setFilterStartDate(contextStart);
      setFilterEndDate(contextEnd);
    }
  }, []);

  useEffect(() => {
    if (hasProps && propStartDate != null && propEndDate != null) {
      const startDateString = toDateStringSafe(propStartDate);
      const endDateString = toDateStringSafe(propEndDate);
      if (startDateString != null && endDateString != null) {
        setLocalStartDate(startDateString);
        setLocalEndDate(endDateString);
        void setUp(startDateString, endDateString);
      }
    } else if (!hasProps && contextStart && contextEnd) {
      if (!hasLoadedInitial.current) {
        hasLoadedInitial.current = true;
        void setUp(contextStart, contextEnd);
      }
    }
  }, [
    hasProps,
    propStartDate?.getTime(),
    propEndDate?.getTime(),
    contextStart,
    contextEnd,
  ]);

  if (loading) return <CenteredCircularProgress type="layout" />;

  const filters = [
    {
      type: 'date' as const,
      label: 'Fecha de inicio',
      dateKey: 'startDate',
      value: filterValueStart,
      setter: (val: string | null) => {
        if (hasProps) setLocalStartDate(val);
        else setFilterStartDate(val);
      },
    },
    {
      type: 'date' as const,
      label: 'Fecha de fin',
      dateKey: 'endDate',
      value: filterValueEnd,
      setter: (val: string | null) => {
        if (hasProps) setLocalEndDate(val);
        else setFilterEndDate(val);
      },
    },
  ];

  const actionButtons: ActionButton[] = [
    {
      label: 'Exportar',
      onClick: () => {
        if (startDate && endDate) {
          void exportToExcel(
            data.summedLines,
            new Date(startDate),
            new Date(endDate),
            user?.businessIdentifier,
            user?.companyName,
          );
        }
      },
    },
  ];

  return (
    <div>
      <div>
        <PageHeader.WithTitleAndActions
          title="Balance"
          subtitle={`Período: ${startDate ? formatDateString(startDate, 'dd/mm/yyyy') : ''} - ${endDate ? formatDateString(endDate, 'dd/mm/yyyy') : ''}`}
          iconType="calendar"
          actionButtons={actionButtons}
          globalSearcherProps={{
            filters,
            triggerSearch: async () => {
              const start = hasProps ? startDate : filterStartDate;
              const end = hasProps ? endDate : filterEndDate;
              if (start && end) {
                if (!hasProps) setPeriod(start, end);
                await setUp(start, end);
              }
            },
            shortcuts: true,
          }}
        />
      </div>
      <FinancialStatementBookEntriesTable
        entries={data}
        refreshData={refreshData}
      />
    </div>
  );
};

export default FinancialStatementBook;
